import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, Palette, FileSpreadsheet } from "lucide-react";
import { api, downloadFile, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Section } from "@/components/settings/Section";
import { openUpgrade } from "@/components/sg/UpgradeDialog";

function Locked() {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 text-sm" data-testid="branding-locked">
      <Lock className="h-4 w-4 text-amber-600 mt-0.5" />
      <div>
        <div className="font-semibold">Super Premium feature</div>
        <div className="text-muted-foreground mt-0.5">Add your brand colour, a “Pay now” button, payment instructions and a late-fee note to every client portal and invoice, remove ScopeGuard branding, and export your income for taxes.</div>
        <Button size="sm" data-testid="branding-unlock-btn" onClick={() => openUpgrade("Branding & getting paid")} className="mt-3 bg-indigo-600 hover:bg-indigo-500 text-white">Unlock with Super Premium</Button>
      </div>
    </div>
  );
}

export function BrandingSettings() {
  const [f, setF] = useState(null);
  useEffect(() => { api.get("/profile/branding").then((r) => setF(r.data)); }, []);
  if (!f) return null;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    try {
      const r = await api.patch("/profile/branding", { brand_color: f.brand_color || "", payment_link: f.payment_link || "", payment_instructions: f.payment_instructions || "", late_fee_note: f.late_fee_note || "", white_label: f.white_label });
      setF(r.data); toast.success("Branding saved");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const exportCsv = () => downloadFile("/export/income.csv", "scopeguard-income.csv").catch(() => toast.error("Export failed"));
  return (
    <Section title="Branding & getting paid" desc="Shown on your client portal and invoices." testId="branding-settings"
      right={<span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400 border border-amber-500/40 rounded px-2 py-1">Super Premium</span>}>
      {!f.unlocked ? <Locked /> : (
        <div className="space-y-4">
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <Label>Brand colour</Label>
              <div className="flex gap-2 mt-1">
                <input type="color" data-testid="brand-color-picker" value={f.brand_color || "#4f46e5"} onChange={set("brand_color")} className="h-10 w-12 rounded border border-border bg-transparent cursor-pointer" />
                <Input data-testid="brand-color-input" value={f.brand_color} onChange={set("brand_color")} placeholder="#4F46E5" />
              </div>
            </div>
            <div className="md:col-span-2"><Label>“Pay now” link</Label><Input data-testid="payment-link-input" className="mt-1" value={f.payment_link} onChange={set("payment_link")} placeholder="https://buy.stripe.com/... or https://paypal.me/..." /></div>
          </div>
          <div><Label>Payment instructions</Label><Textarea data-testid="payment-instructions-input" value={f.payment_instructions} onChange={set("payment_instructions")} maxLength={500} className="min-h-[80px] mt-1" placeholder="e.g. Zelle to pay@mycompany.com, or checks payable to Rivera Remodeling LLC" /></div>
          <div>
            <Label>Late-fee note for overdue milestones <span className="text-muted-foreground">(optional)</span></Label>
            <Textarea data-testid="late-fee-note-input" value={f.late_fee_note} onChange={set("late_fee_note")} maxLength={300} className="min-h-[60px] mt-1" placeholder="e.g. Per our agreement, a 5% late fee applies to balances unpaid 7 days after approval is due." />
            <div className="text-xs text-muted-foreground mt-1">Added to reminder emails and the client portal only once a milestone is overdue (72h+). Make sure it matches what your client agreed to.</div>
          </div>
          <div className="flex items-center justify-between border border-border rounded-lg p-3">
            <div><div className="text-sm font-medium"><Palette className="h-3.5 w-3.5 inline mr-1" />Remove ScopeGuard branding</div><div className="text-xs text-muted-foreground">Clients only see your business on the portal and invoices</div></div>
            <Switch data-testid="white-label-switch" checked={f.white_label} onCheckedChange={(v) => setF({ ...f, white_label: v })} />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button data-testid="branding-save-btn" onClick={save} className="bg-indigo-600 hover:bg-indigo-500 text-white">Save branding</Button>
            <Button data-testid="export-income-btn" variant="outline" onClick={exportCsv}><FileSpreadsheet className="h-4 w-4 mr-2" />Export income (CSV)</Button>
          </div>
        </div>
      )}
    </Section>
  );
}
